import { FC } from 'react';
import React from 'react';

import { IntlShape, useIntl } from 'react-intl';

import { ICartEntry } from '@rbi-ctg/menu';
import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { useAuthContext } from 'state/auth';
import { IncentiveEvaluationErrorCodes, IncentiveEvaluationMap } from 'state/loyalty/hooks/types';
import { getEvaluationResultMessage } from 'state/loyalty/hooks/utils/helpers';
import { ISanityRewardsMap, LoyaltyReward } from 'state/loyalty/types';
import { isMobile } from 'utils/environment';

import { LoyaltyRewardsCategoryCarousel } from './loyalty-rewards-category-carousel';
import { LoyaltyRewardsCategoryList } from './loyalty-rewards-category-list';
import { LoyaltyRewardsMeter } from './loyalty-rewards-meter';
import {
  CategoryLabel,
  CrownIcon,
  RewardCategoryContainer,
  RewardCategoryHeader,
} from './loyalty-rewards.styled';
import {
  EnrichedReward,
  ILoyaltyRewardsList,
  NonNullableReward,
  NonNullableRewardCategory,
} from './types';

/**
 * Enrich reward with extra information, including inline alert message based on availability
 */
const enrichReward = ({
  reward,
  sanityRewardsMap,
  rewardsEvaluationMap,
  formatters,
  cartEntries,
}: {
  reward: NonNullableReward;
  sanityRewardsMap: ISanityRewardsMap | null;
  rewardsEvaluationMap: IncentiveEvaluationMap;
  formatters: IntlShape;
  cartEntries: ICartEntry[];
}): EnrichedReward => {
  const { formatMessage } = formatters;
  const rewardId = reward?._id;
  const incentive: LoyaltyReward | undefined = sanityRewardsMap?.[rewardId || ''];
  const loyaltyRewardId = incentive?.loyaltyEngineId || '';
  const rewardEvaluationResults = rewardsEvaluationMap[loyaltyRewardId];

  const evaluationMessages = rewardEvaluationResults?.map(evaluationResult => {
    const { code, targetValue } = evaluationResult;
    if (code === IncentiveEvaluationErrorCodes.OUT_OF_DAY_PART) {
      const { incentive } = targetValue;
      const dayPart = incentive.incentives[0]?.operationalItem?.daypart[0];
      return `${dayPart} ${formatMessage({ id: 'only' })}`;
    } else if (code === IncentiveEvaluationErrorCodes.NOT_AVAILABLE_IN_STORE) {
      return formatMessage({ id: 'rewardUnavailable' });
    }
    // Fallback for other messaging
    const message = getEvaluationResultMessage({
      evaluationResult,
      formatters,
      incentiveType: LoyaltyPromotionType.OFFER,
      cartEntries,
    });
    return message as string;
  });
  const inlineAlertMessage = evaluationMessages ? evaluationMessages[0] : undefined;

  return {
    reward,
    rewardId,
    incentive,
    loyaltyRewardId,
    rewardEvaluationResults,
    inlineAlertMessage,
  };
};

/**
 * Comparator to sort rewards by inline alert messages
 */
const compareInlineAlerts = (a: string | undefined, b: string | undefined) => {
  if (!a && b) {
    return -1;
  } else if (a && !b) {
    return 1;
  } else if (a && b) {
    return a.localeCompare(b);
  }
  return 0;
};

export const LoyaltyRewardsList: FC<ILoyaltyRewardsList> = ({
  handleOnPress,
  rewardCategories,
  engineRewardsMap,
  rewardsEvaluationMap,
  sanityRewardsMap,
}) => {
  const formatters = useIntl();
  const { formatMessage } = formatters;
  const { isAuthenticated } = useAuthContext();
  const { cartEntries } = useCart();

  // Only show carousel if on Desktop web
  const showCarousel = !isMobile();

  // Find first unavailable category to display progress meter
  const firstUnavailableCategory = rewardCategories.find((category: NonNullableRewardCategory) => {
    if (!category.rewards?.length || !category.label?.en) {
      return false;
    }
    const engineRewardItem = engineRewardsMap[category?.rewards[0]?.loyaltyEngineId || ''];
    if (engineRewardItem.locked) {
      return true;
    }
    return false;
  });

  return rewardCategories.map((category: NonNullableRewardCategory) => {
    const categoryLabel = category.label?.en;

    if (!category.rewards?.length || !categoryLabel) {
      return null;
    }

    const categoryHeaderAccessibilityHint = `${categoryLabel} ${formatMessage({
      id: 'crowns',
    })}`;

    const engineRewardItem = engineRewardsMap[category.rewards[0]?.loyaltyEngineId || ''];
    const { locked, redemptionPercentage, remainingPointsNeededForRedemption } = engineRewardItem;
    const showMeter = locked && firstUnavailableCategory?.label?.en === categoryLabel;

    // Enrich rewards with extra information, including inline alert messages
    const rewards = category.rewards
      .map(reward => {
        return enrichReward({
          reward: reward as NonNullableReward,
          sanityRewardsMap,
          rewardsEvaluationMap,
          formatters,
          cartEntries,
        });
      })
      .sort((a, b) => {
        return compareInlineAlerts(a.inlineAlertMessage, b.inlineAlertMessage);
      });

    return (
      <React.Fragment key={categoryLabel}>
        {showMeter ? (
          <LoyaltyRewardsMeter
            redemptionPercentage={redemptionPercentage || 0}
            remainingPointsNeededForRedemption={remainingPointsNeededForRedemption || undefined}
          ></LoyaltyRewardsMeter>
        ) : null}

        <RewardCategoryContainer locked={locked || false} accessible={false}>
          <RewardCategoryHeader>
            <CrownIcon />
            <CategoryLabel accessibilityLabel={categoryHeaderAccessibilityHint}>
              {categoryLabel}
            </CategoryLabel>
          </RewardCategoryHeader>

          {showCarousel ? (
            <LoyaltyRewardsCategoryCarousel
              rewards={rewards}
              locked={!isAuthenticated || locked || false}
              handleOnPress={handleOnPress}
            />
          ) : (
            <LoyaltyRewardsCategoryList
              rewards={rewards}
              locked={!isAuthenticated || locked || false}
              handleOnPress={handleOnPress}
            />
          )}
        </RewardCategoryContainer>
      </React.Fragment>
    );
  });
};
